import { createStore } from 'vuex';
import Cookies from 'js-cookie';
import { verifyPermissions } from '@/services/auth';
import { getUnreadCount } from '@/services/notifications';

const cookieOptions = {
  expires: 7,
  sameSite: 'Lax',
  secure: process.env.NODE_ENV === 'production',
  path: '/'
};

export default createStore({
  state: {
    cart: JSON.parse(Cookies.get('cart') || '[]'),
    user: null,
    userPermissions: null,
    isLoadingPermissions: false,
    unreadNotifications: 0,
  },
  getters: {
    cartCount: (state) => {
      return state.cart.reduce((total, item) => total + item.quantity, 0);
    },
    cartTotal: (state) => {
      return state.cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    },
    cartItems: (state) => state.cart,
    isCartOpen: (state) => state.isCartOpen,
    isAdmin: (state) => {
      if (!state.userPermissions) return false;
      return state.userPermissions.is_staff || state.userPermissions.is_superuser;
    },
    hasPermission: (state) => (permission) => {
      if (!state.userPermissions) return false;
      return state.userPermissions.permissions[permission] || false;
    },
    userInfo: (state) => state.user,
    unreadNotifications: (state) => state.unreadNotifications,
  },
  mutations: {
    ADD_TO_CART(state, item) {
      // Validar que el precio sea mayor a 0
      if (!item.price || item.price <= 0) {
        console.warn('No se puede agregar al carrito un producto con precio 0 o inválido');
        return;
      }
      
      const existingItem = state.cart.find(i => i.id === item.id && i.blister_id === item.blister_id);
      if (existingItem) {
        existingItem.quantity += item.quantity || 1;
      } else {
        state.cart.push({
          id: item.id,
          blister_id: item.blister_id,
          name: item.name,
          variant_label: item.variant_label || '',
          price: item.price,
          brand: item.brand,
          image: item.image,
          quantity: item.quantity || 1
        });
      }
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    REMOVE_FROM_CART(state, { productId, blisterId }) {
      state.cart = state.cart.filter(item => item.id !== productId || item.blister_id !== blisterId);
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    UPDATE_QUANTITY(state, { productId, blisterId, quantity }) {
      const product = state.cart.find(item => item.id === productId && item.blister_id === blisterId);
      if (product) {
        product.quantity = Math.max(0, quantity);
        if (product.quantity === 0) {
          state.cart = state.cart.filter(item => item.id !== productId || item.blister_id !== blisterId);
        }
      }
      Cookies.set('cart', JSON.stringify(state.cart), cookieOptions);
    },
    CLEAR_CART(state) {
      state.cart = [];
      Cookies.remove('cart', cookieOptions);
    },
    SET_USER(state, user) {
      state.user = user;
    },
    SET_USER_PERMISSIONS(state, permissions) {
      state.userPermissions = permissions;
    },
    SET_LOADING_PERMISSIONS(state, loading) {
      state.isLoadingPermissions = loading;
    },
    CLEAR_USER_DATA(state) {
      state.user = null;
      state.userPermissions = null;
      state.unreadNotifications = 0;
    },
    SET_UNREAD_NOTIFICATIONS(state, count) {
      state.unreadNotifications = count;
    },
  },
  actions: {
    addToCart({ commit }, product) {
      // Validación adicional antes de agregar
      if (!product.price || product.price <= 0) {
        throw new Error('No se puede agregar un producto sin precio al carrito');
      }
      commit('ADD_TO_CART', product);
    },
    removeFromCart({ commit }, payload) {
      commit('REMOVE_FROM_CART', payload);
    },
    updateQuantity({ commit }, payload) {
      commit('UPDATE_QUANTITY', payload);
    },
    clearCart({ commit }) {
      commit('CLEAR_CART');
    },
    async loadUserPermissions({ commit }) {
      try {
        commit('SET_LOADING_PERMISSIONS', true);
        const permissions = await verifyPermissions();
        commit('SET_USER_PERMISSIONS', permissions);
        commit('SET_USER', {
          id: permissions.user_id,
          username: permissions.username,
          email: permissions.email,
          first_name: permissions.first_name,
          last_name: permissions.last_name,
          is_staff: permissions.is_staff,
          is_superuser: permissions.is_superuser,
        });
        return permissions;
      } catch (error) {
        console.error('Error loading user permissions:', error);
        commit('CLEAR_USER_DATA');
        throw error;
      } finally {
        commit('SET_LOADING_PERMISSIONS', false);
      }
    },
    setUser({ commit }, user) {
      commit('SET_USER', user);
    },
    clearUserData({ commit }) {
      commit('CLEAR_USER_DATA');
    },
    async fetchUnreadNotifications({ commit }) {
      try {
        const count = await getUnreadCount();
        commit('SET_UNREAD_NOTIFICATIONS', count);
      } catch {
        // silently ignore — user may not be authenticated
      }
    },
  }
});
